import {
  ChangeDetectorRef,
  Directive,
  effect,
  inject,
  input,
} from '@angular/core';
import { isNil, isNotNil, Nillable } from '@web/utils';
import { SidebarSize } from '../types/sidebar-size';
import { Drawer } from 'primeng/drawer';

@Directive({
  selector: '[appDrawerSidebar]',
  standalone: true,
})
export class PrimengDrawerDirective {
  private readonly drawer = inject(Drawer);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  public readonly size = input<Nillable<SidebarSize>>(null);
  public readonly customCloseEvent = input<Nillable<(event?: Event) => void>>();
  public readonly blockScroll = input<boolean>(false);
  public readonly dismissible = input<boolean>(false);
  public readonly closeOnEscape = input<boolean>(false);
  public readonly noContentPadding = input<boolean>(false);

  private readonly originalCloseEvent: (event: Event) => void;
  private readonly initialStyleClassList = this.getStyleClassList();

  public constructor() {
    this.originalCloseEvent = this.prepareOriginalCloseEvent();

    effect(() => this.configureStyleClass());
    effect(() => this.configureCustomCloseEvent());
    effect(() => this.configureBlockScroll());
    effect(() => this.configureDismissible());
    effect(() => this.configureCloseOnEsc());
  }

  private configureStyleClass(): void {
    const styles = [
      ...this.initialStyleClassList,
      ...this.getDefaultStyleClassList(),
      ...this.getNoContentPaddingClassList(),
    ];

    this.drawer.styleClass = styles.filter(isNotNil).join(' ');

    this.changeDetectorRef.detectChanges();
  }

  private configureCustomCloseEvent(): void {
    const customCloseEvent = this.customCloseEvent();

    if (isNil(customCloseEvent)) {
      this.drawer.close = this.originalCloseEvent;

      return;
    }

    this.drawer.close = (event: Event) => {
      this.originalCloseEvent(event);
      customCloseEvent(event);
    };
  }

  private configureBlockScroll(): void {
    this.drawer.blockScroll = this.blockScroll();
  }

  private configureDismissible(): void {
    this.drawer.dismissible = this.dismissible();
  }

  private configureCloseOnEsc(): void {
    this.drawer.closeOnEscape = this.closeOnEscape();
  }

  private getDefaultStyleClassList(): string[] {
    switch (this.size()) {
      case 'sm':
        return ['xl:w-3/12 2xl:max-w-screen-2xl w-full'];

      case 'lg':
        return ['xl:w-5/12 2xl:max-w-screen-2xl w-full'];

      case 'xl':
        return ['xl:w-8/12 2xl:max-w-screen-2xl w-full'];

      case 'xxl':
        return ['xl:w-11/12 2xl:max-w-screen-2xl w-full'];

      case '3xl':
        return ['xl:w-11/12 2xl:max-w-3/4 w-full'];

      default:
        return [];
    }
  }

  private getNoContentPaddingClassList(): string[] {
    if (this.noContentPadding()) {
      return ['no-content-padding'];
    }

    return [];
  }

  private getStyleClassList(): string[] {
    return this.drawer.styleClass?.split(' ') ?? [];
  }

  private prepareOriginalCloseEvent(): (event: Event) => void {
    return this.drawer.close.bind(this.drawer);
  }
}
